import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sendMigrationOtp, completeEmailMigration } from '../utils/api';
import GoogleButton from './GoogleButton';
import Input from './Input';
import Button from './Button';

const AccountMigrationOverlay = () => {
  const { user, updateUser, logout } = useAuth();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('google'); // 'google' | 'email'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    otp: ''
  });
  const [step, setStep] = useState(1); // 1: form, 2: otp
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 1. Strict check: ONLY show for ORCID users who still have a dummy placeholder email
  const isUnmigratedOrcidUser = Boolean(
    user &&
    user.orcid &&
    user.email &&
    (user.email.endsWith('@orcid.user') || user.email.includes('@orcid.user'))
  );

  // 2. Do not block while processing OAuth callbacks
  const isAuthCallback = location.pathname.startsWith('/auth/');

  if (!isUnmigratedOrcidUser || isAuthCallback) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validatePassword = (pwd) => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(pwd)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number';
    if (!/[@$!%*?&]/.test(pwd)) return 'Password must contain at least one special character (@$!%*?&)';
    return null;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      setError('Please provide a valid email address.');
      return;
    }
    if (trimmedEmail.endsWith('@orcid.user')) {
      setError('Please provide a real email address (e.g. Gmail, University, or Company email).');
      return;
    }

    const pwdError = validatePassword(formData.password);
    if (pwdError) {
      setError(pwdError);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setLoading(true);
      const res = await sendMigrationOtp(trimmedEmail);
      if (res.success) {
        setStep(2);
        setSuccess('Verification code sent! Please check your inbox.');
      } else {
        setError(res.message || 'Failed to send verification code.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send verification code. The email may already be in use.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.otp.trim()) {
      setError('Please enter the 6-digit verification code.');
      return;
    }

    try {
      setLoading(true);
      const res = await completeEmailMigration({
        email: formData.email.trim(),
        otp: formData.otp.trim(),
        password: formData.password
      });

      if (res.success) {
        setSuccess('Credentials successfully updated! Unlocking your dashboard...');
        if (res.data?.token) {
          localStorage.setItem('token', res.data.token);
        }
        if (res.data?.user) {
          localStorage.setItem('user', JSON.stringify(res.data.user));
          if (updateUser) updateUser(res.data.user);
        }
      } else {
        setError(res.message || 'Failed to verify code.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired verification code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      style={{ margin: 0 }}
      role="dialog"
      aria-modal="true"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 sm:p-8 overflow-hidden border border-gray-100 my-auto">
        {/* Header - Non-closable (No close 'X' button) */}
        <div className="pb-4 border-b border-gray-100">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 mb-3">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Action Required
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            Action Required: Complete Account Setup
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Your account was registered via ORCID. Because ORCID does not share your email, you must add a verified email address to receive paper decisions, review assignments, and conference updates before accessing the system.
          </p>
        </div>

        {/* Current Account Details Box */}
        <div className="my-4 p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-700 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-800">Your ORCID iD:</span>
            <span className="font-mono text-primary-700 font-semibold">{user.orcid}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-800">Assigned Placeholder:</span>
            <span className="font-mono text-amber-700 font-semibold">{user.email}</span>
          </div>
          <p className="text-[11px] text-amber-800 pt-1 border-t border-slate-200">
            ⚠️ This dummy email cannot receive emails. Please connect Google or enter a real email below.
          </p>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-gray-200 mb-5">
          <button
            type="button"
            onClick={() => { setActiveTab('google'); setError(''); setSuccess(''); }}
            className={`flex-1 py-2.5 text-center font-semibold text-sm border-b-2 transition-colors ${
              activeTab === 'google'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Option 1: Connect Google
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('email'); setError(''); setSuccess(''); }}
            className={`flex-1 py-2.5 text-center font-semibold text-sm border-b-2 transition-colors ${
              activeTab === 'email'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Option 2: Email & Password
          </button>
        </div>

        {/* Feedback Messages */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-2.5 rounded-lg flex items-start gap-2">
            <svg className="w-5 h-5 flex-shrink-0 text-red-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-2.5 rounded-lg flex items-start gap-2">
            <svg className="w-5 h-5 flex-shrink-0 text-green-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>{success}</span>
          </div>
        )}

        {/* Tab 1: Google Linking */}
        {activeTab === 'google' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Link your Google account in one click. We will automatically replace your placeholder email with your verified Google email address.
            </p>
            <div className="pt-2">
              <GoogleButton
                mode="link"
                buttonText="Connect with Google (1-Click)"
              />
            </div>
            <p className="text-xs text-gray-500 text-center">
              You will still be able to sign in using ORCID or Google anytime.
            </p>
          </div>
        )}

        {/* Tab 2: Email + Password */}
        {activeTab === 'email' && (
          <div>
            {step === 1 ? (
              <form onSubmit={handleSendOtp} className="space-y-4">
                <Input
                  label="Your Real Email Address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="e.g. author@university.edu"
                  required
                />
                <Input
                  label="Create a Password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min. 8 characters (A-Z, a-z, 0-9, special)"
                  required
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  required
                />
                <Button
                  type="submit"
                  loading={loading}
                  className="w-full"
                >
                  Send Verification Code
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  We sent a 6-digit code to <strong>{formData.email}</strong>. Enter it below to verify and complete your migration.
                </div>
                <Input
                  label="6-Digit Verification Code (OTP)"
                  type="text"
                  name="otp"
                  maxLength="6"
                  value={formData.otp}
                  onChange={handleChange}
                  placeholder="123456"
                  required
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setStep(1)}
                    disabled={loading}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    loading={loading}
                    className="flex-1"
                  >
                    Verify & Unlock Dashboard
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Footer with Logout and Integrity Reassurance */}
        <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-2">
          <span>✓ Submissions and reviews remain linked</span>
          <button
            type="button"
            onClick={logout}
            className="text-red-600 hover:text-red-700 font-medium underline"
          >
            Log out for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountMigrationOverlay;
